import { useState, useEffect } from 'react';
import { AlertCircle, Clock, CheckCircle, FileText, Wrench, User, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/apiClient';

interface MaintenanceRequest {
  id: number;
  category: string;
  urgency: string;
  priority: number;
  status: string;
  description: string;
  propertyAddress?: string;
  renterName?: string;
  createdAt: string;
  slaDue?: string;
  slaExpired?: boolean;
  complianceRequired?: boolean;
  assignedContractorId?: number;
  quoteAmount?: number;
  quoteStatus?: string;
}

interface DashboardData {
  newIssues: MaintenanceRequest[];
  pendingQuotes: MaintenanceRequest[];
  overdueTasks: MaintenanceRequest[];
  complianceRequired: MaintenanceRequest[];
  stats: {
    newCount: number;
    pendingQuotesCount: number;
    overdueCount: number;
    complianceCount: number;
    totalActive: number;
  };
}

export default function AgentMaintenanceDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'new' | 'quotes' | 'overdue' | 'compliance'>('new');

  useEffect(() => {
    loadDashboard();
    // Refresh every 30 seconds
    const interval = setInterval(loadDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await api.get<DashboardData>('/agent/maintenance/dashboard');
      setDashboard(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency?.toUpperCase()) {
      case 'EMERGENCY': return '#DC2626';
      case 'HIGH': return '#DF6C4F';
      case 'MEDIUM': return '#F59E0B';
      case 'LOW': return '#49c5b6';
      default: return '#666';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': return <CheckCircle className="w-5 h-5" style={{ color: '#49c5b6' }} />;
      case 'IN_PROGRESS': return <Clock className="w-5 h-5" style={{ color: '#F59E0B' }} />;
      case 'ASSIGNED': return <User className="w-5 h-5" style={{ color: '#0C4432' }} />;
      default: return <AlertCircle className="w-5 h-5" style={{ color: '#DF6C4F' }} />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const renderRequestCard = (request: MaintenanceRequest) => (
    <div
      key={request.id}
      onClick={() => navigate(`/agent/maintenance/${request.id}`)}
      className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {getStatusIcon(request.status)}
          <span className="font-semibold" style={{ color: '#0C4432' }}>
            #{request.id}
          </span>
          <span
            className="px-2 py-1 text-xs rounded-full font-medium"
            style={{ backgroundColor: getUrgencyColor(request.urgency) + '20', color: getUrgencyColor(request.urgency) }}
          >
            {request.urgency}
          </span>
          {request.complianceRequired && (
            <span className="px-2 py-1 text-xs rounded-full font-medium" style={{ backgroundColor: '#FEE', color: '#DC2626' }}>
              Compliance
            </span>
          )}
        </div>
        <ChevronRight className="w-5 h-5" style={{ color: '#666' }} />
      </div>

      <p className="font-medium mb-1" style={{ color: '#222' }}>{request.category}</p>
      <p className="text-sm mb-2" style={{ color: '#666' }}>
        {request.description.substring(0, 80)}...
      </p>

      <div className="flex items-center justify-between text-xs" style={{ color: '#666' }}>
        <span>{request.propertyAddress}</span>
        <span>{formatDate(request.createdAt)}</span>
      </div>

      {request.slaExpired && (
        <div className="mt-2 px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: '#FEE', color: '#DC2626' }}>
          ⚠️ SLA Expired
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#0C4432' }}></div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <p style={{ color: '#666' }}>No data available</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F8F8F8' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Wrench className="w-8 h-8" style={{ color: '#0C4432' }} />
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#0C4432' }}>Maintenance Dashboard</h1>
            <p style={{ color: '#666' }}>Triage and manage maintenance requests</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-md" style={{ backgroundColor: '#FEE', color: '#DF6C4F' }}>
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div
            className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${activeTab === 'new' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setActiveTab('new')}
          >
            <div className="flex items-center justify-between mb-2">
              <AlertCircle className="w-6 h-6" style={{ color: '#DF6C4F' }} />
              <span className="text-2xl font-bold" style={{ color: '#0C4432' }}>
                {dashboard.stats.newCount}
              </span>
            </div>
            <p className="text-sm font-medium" style={{ color: '#666' }}>New Issues</p>
          </div>

          <div
            className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${activeTab === 'quotes' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setActiveTab('quotes')}
          >
            <div className="flex items-center justify-between mb-2">
              <FileText className="w-6 h-6" style={{ color: '#F59E0B' }} />
              <span className="text-2xl font-bold" style={{ color: '#0C4432' }}>
                {dashboard.stats.pendingQuotesCount}
              </span>
            </div>
            <p className="text-sm font-medium" style={{ color: '#666' }}>Pending Quotes</p>
          </div>

          <div
            className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${activeTab === 'overdue' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setActiveTab('overdue')}
          >
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-6 h-6" style={{ color: '#DC2626' }} />
              <span className="text-2xl font-bold" style={{ color: '#0C4432' }}>
                {dashboard.stats.overdueCount}
              </span>
            </div>
            <p className="text-sm font-medium" style={{ color: '#666' }}>Overdue Tasks</p>
          </div>

          <div
            className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${activeTab === 'compliance' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setActiveTab('compliance')}
          >
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-6 h-6" style={{ color: '#49c5b6' }} />
              <span className="text-2xl font-bold" style={{ color: '#0C4432' }}>
                {dashboard.stats.complianceCount}
              </span>
            </div>
            <p className="text-sm font-medium" style={{ color: '#666' }}>Compliance Items</p>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <Wrench className="w-6 h-6" style={{ color: '#0C4432' }} />
              <span className="text-2xl font-bold" style={{ color: '#0C4432' }}>
                {dashboard.stats.totalActive}
              </span>
            </div>
            <p className="text-sm font-medium" style={{ color: '#666' }}>Total Active</p>
          </div>
        </div>

        {/* Request Lists */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b">
            <div className="flex">
              <button
                onClick={() => setActiveTab('new')}
                className={`px-6 py-3 font-medium ${activeTab === 'new' ? 'border-b-2' : ''}`}
                style={{
                  color: activeTab === 'new' ? '#0C4432' : '#666',
                  borderColor: activeTab === 'new' ? '#0C4432' : 'transparent'
                }}
              >
                New Issues ({dashboard.stats.newCount})
              </button>
              <button
                onClick={() => setActiveTab('quotes')}
                className={`px-6 py-3 font-medium ${activeTab === 'quotes' ? 'border-b-2' : ''}`}
                style={{
                  color: activeTab === 'quotes' ? '#0C4432' : '#666',
                  borderColor: activeTab === 'quotes' ? '#0C4432' : 'transparent'
                }}
              >
                Pending Quotes ({dashboard.stats.pendingQuotesCount})
              </button>
              <button
                onClick={() => setActiveTab('overdue')}
                className={`px-6 py-3 font-medium ${activeTab === 'overdue' ? 'border-b-2' : ''}`}
                style={{
                  color: activeTab === 'overdue' ? '#0C4432' : '#666',
                  borderColor: activeTab === 'overdue' ? '#0C4432' : 'transparent'
                }}
              >
                Overdue ({dashboard.stats.overdueCount})
              </button>
              <button
                onClick={() => setActiveTab('compliance')}
                className={`px-6 py-3 font-medium ${activeTab === 'compliance' ? 'border-b-2' : ''}`}
                style={{
                  color: activeTab === 'compliance' ? '#0C4432' : '#666',
                  borderColor: activeTab === 'compliance' ? '#0C4432' : 'transparent'
                }}
              >
                Compliance ({dashboard.stats.complianceCount})
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'new' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold mb-4" style={{ color: '#0C4432' }}>
                  New Issues Requiring Triage
                </h2>
                {dashboard.newIssues.length === 0 ? (
                  <p className="text-center py-8" style={{ color: '#666' }}>No new issues</p>
                ) : (
                  dashboard.newIssues.map(renderRequestCard)
                )}
              </div>
            )}

            {activeTab === 'quotes' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold mb-4" style={{ color: '#0C4432' }}>
                  Pending Contractor Quotes
                </h2>
                {dashboard.pendingQuotes.length === 0 ? (
                  <p className="text-center py-8" style={{ color: '#666' }}>No pending quotes</p>
                ) : (
                  dashboard.pendingQuotes.map(renderRequestCard)
                )}
              </div>
            )}

            {activeTab === 'overdue' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold mb-4" style={{ color: '#0C4432' }}>
                  Overdue Tasks (SLA Expired)
                </h2>
                {dashboard.overdueTasks.length === 0 ? (
                  <p className="text-center py-8" style={{ color: '#666' }}>No overdue tasks</p>
                ) : (
                  dashboard.overdueTasks.map(renderRequestCard)
                )}
              </div>
            )}

            {activeTab === 'compliance' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold mb-4" style={{ color: '#0C4432' }}>
                  Compliance-Required Items
                </h2>
                {dashboard.complianceRequired.length === 0 ? (
                  <p className="text-center py-8" style={{ color: '#666' }}>No compliance items</p>
                ) : (
                  dashboard.complianceRequired.map(renderRequestCard)
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

